import { Component, forwardRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { PALETTE_SWATCHES, PaletteSwatch } from "../../../model/palette";

/**
 * A colour field that offers the customer's palette and nothing else.
 *
 * Statuses and priorities used to get a free colour picker, so every screen
 * that draws them could end up in any colour at all. The customer now has a
 * brandbook (#173), and what can be chosen is its eleven colours.
 *
 * A colour stored before this came, from outside the palette, is left as it is:
 * nothing is selected, and a line says the current colour is not one of these.
 * It changes the moment someone picks one.
 */
@Component({
    selector: "palette-swatches",
    standalone: true,
    imports: [CommonModule, TranslateModule],
    templateUrl: "./palette-swatches.component.html",
    styleUrls: ["./palette-swatches.component.css"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PaletteSwatchesComponent),
            multi: true,
        },
    ],
})
export class PaletteSwatchesComponent implements ControlValueAccessor {
    readonly swatches = PALETTE_SWATCHES;
    value = "";
    disabled = false;

    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(value: string | null): void {
        this.value = (value ?? "").trim().toLowerCase();
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    choose(swatch: PaletteSwatch): void {
        if (this.disabled) {
            return;
        }
        this.value = swatch.hex;
        this.onChange(swatch.hex);
        this.onTouched();
    }

    isChosen(swatch: PaletteSwatch): boolean {
        return this.value === swatch.hex;
    }

    /** The name of the chosen colour, or empty when none of the palette is chosen. */
    get chosenName(): string {
        return this.swatches.find((swatch) => this.isChosen(swatch))?.name ?? "";
    }

    /** Whether a colour is stored that the palette does not have. */
    get outsidePalette(): boolean {
        return !!this.value && !this.chosenName;
    }
}
